// Assets
import AddTimecardIcon from 'assets/images/icons/AddTimecardIcon.svg?react';
import CreditCardIcon from 'assets/images/icons/CreditCardIcon.svg?react';
import PlayWithPlusIcon from 'assets/images/icons/PlayWithPlusIcon.svg?react';

// Project imports
import {
    CallForActionButton,
    CallForActionButtonTooltip,
    TableRowCallForActionButton,
} from 'components/buttons/CallForActionButton';
import { CirclePlayIcon, MoreVert as MoreVertIcon, ReceiptIcon, TimerIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { ComponentProps, ReactNode } from 'react';
import { t } from 'utils/getTranslations';

// Types
import { CloseCaseDropdownItem, useMayOfferCloseCase } from 'pages/matters/closing/CloseCaseMenuItem';
import { useAddAndStartTimecard } from 'pages/timecards/useAddAndStartTimecard';
import { MatterListItem } from 'types/matters';
import { useHasPermissions } from 'utils/permissionUtil';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

type Props = {
    matter: MatterListItem;
    /** Whether the user may close this case, as `useClosableMatterIds` answers for the whole list. */
    canCloseCase: boolean;
    /** Plain 32px icon buttons with outline glyphs, as Home draws them, instead of the round tinted ones. */
    plain?: boolean;
};

const MyCaseActionButtons = ({ matter, canCloseCase, plain }: Props): JSX.Element => {
    return (
        <>
            <AddExpenseButton matter={matter} plain={plain} />
            <AddNewTimecardButton matter={matter} plain={plain} />
            <AddAndStartTimecardButton matter={matter} plain={plain} />
            <CaseRowMenu matter={matter} canCloseCase={canCloseCase} />
        </>
    );
};

interface MatterProps {
    matter: MatterListItem;
    plain?: boolean;
}

type ActionButtonProps = Pick<ComponentProps<'button'>, 'aria-label' | 'disabled' | 'onClick'> & {
    plain?: boolean;
    icon: ReactNode;
    plainIcon: ReactNode;
};

const ActionButton = ({ plain, icon, plainIcon, ...props }: ActionButtonProps): JSX.Element =>
    plain ? (
        <Button variant="ghost" size="icon-sm" className="text-primary hover:text-primary rounded-md" {...props}>
            {plainIcon}
        </Button>
    ) : (
        <CallForActionButton variant="contained" shape="rounded" color="info" {...props}>
            {icon}
        </CallForActionButton>
    );

const PLAIN_ICON_CLASS = 'size-[17px]';

const AddNewTimecardButton = ({ matter, plain }: MatterProps): JSX.Element | null => {
    const navigateWithReturn = useNavigateWithReturnState();

    const hasPermissionCreateTimecards = useHasPermissions(['CREATE.TIMECARDS']);

    const addTimecard = () => {
        navigateWithReturn(`/matter/${matter.id}/timecards/new`);
    };

    return hasPermissionCreateTimecards ? (
        <CallForActionButtonTooltip title={t('dashboard.add-new-timecard')}>
            <ActionButton
                plain={plain}
                icon={<AddTimecardIcon />}
                plainIcon={<TimerIcon className={PLAIN_ICON_CLASS} />}
                aria-label={t('dashboard.add-new-timecard')}
                onClick={addTimecard}
            />
        </CallForActionButtonTooltip>
    ) : null;
};

const AddExpenseButton = ({ matter, plain }: MatterProps): JSX.Element | null => {
    const navigateWithReturn = useNavigateWithReturnState();

    const hasPermissionCreateExpenses = useHasPermissions(['CREATE.EXPENSES']);
    const isExpenseEntryDisabled = useExpenseEntryDisabled();

    const addExpense = () => {
        navigateWithReturn('/expense/new', {
            state: {
                presetMatter: {
                    id: matter.id,
                    name: matter.name,
                    fullName: matter.fullName,
                    isNonBillable: matter.isNonBillable,
                    isConfidential: matter.isConfidential,
                    visibility: matter.visibility,
                },
            },
        });
    };

    return hasPermissionCreateExpenses ? (
        <CallForActionButtonTooltip
            title={isExpenseEntryDisabled ? t('expenses.integration-entry-disabled') : t('dashboard.add-new-expense')}
        >
            <span>
                <ActionButton
                    plain={plain}
                    icon={<CreditCardIcon />}
                    plainIcon={<ReceiptIcon className={PLAIN_ICON_CLASS} />}
                    aria-label={t('dashboard.add-new-expense')}
                    disabled={isExpenseEntryDisabled}
                    onClick={addExpense}
                />
            </span>
        </CallForActionButtonTooltip>
    ) : null;
};

const AddAndStartTimecardButton = ({ matter, plain }: MatterProps): JSX.Element | null => {
    const { addAndStartTimecard, isSubmitting } = useAddAndStartTimecard(matter);

    const hasPermissionCreateTimecards = useHasPermissions(['CREATE.TIMECARDS']);

    return hasPermissionCreateTimecards ? (
        <CallForActionButtonTooltip title={t('dashboard.start-new-timecard')}>
            <ActionButton
                plain={plain}
                icon={<PlayWithPlusIcon />}
                plainIcon={<CirclePlayIcon className={PLAIN_ICON_CLASS} />}
                aria-label={t('dashboard.start-new-timecard')}
                disabled={isSubmitting}
                onClick={() => {
                    void addAndStartTimecard();
                }}
            />
        </CallForActionButtonTooltip>
    ) : null;
};

/** The Cases list's row menu, offered only while it has an item to hold. */
function CaseRowMenu({ matter, canCloseCase }: MatterProps & { canCloseCase: boolean }): JSX.Element | null {
    const mayOfferCloseCase = useMayOfferCloseCase(matter.matterStateTypeCode);

    return mayOfferCloseCase && canCloseCase ? (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <TableRowCallForActionButton shape="rounded" aria-label={t('actions')}>
                        <MoreVertIcon />
                    </TableRowCallForActionButton>
                }
            />
            <DropdownMenuContent align="end" className="w-auto">
                <CloseCaseDropdownItem
                    matterId={matter.id}
                    matterStateTypeCode={matter.matterStateTypeCode}
                    canEditMatter={canCloseCase}
                />
            </DropdownMenuContent>
        </DropdownMenu>
    ) : null;
}

export default MyCaseActionButtons;
